export type WorkbookSheetColumn = {
  letter: string;
  header?: string;
};

export type WorkbookSheetSummary = {
  name: string;
  headerRowValues: string[];
  columns: WorkbookSheetColumn[];
  rowCount: number;
  previewRows: Record<string, string>[];
};

export type SheetConfig = {
  namePattern: string;
  sourceColumns: string[];
  targetColumns?: Record<string, string>;
  createTargetIfMissing?: boolean;
  headerRow?: number;
  valuesStartRow?: number;
  skipHiddenRows?: boolean;
  skipHiddenColumns?: boolean;
  excludeColors?: string[];
  translateComments?: boolean;
  treatMergedRegions?: 'top-left' | 'expand' | 'skip';
  preserveStyles?: boolean;
  inlineCodeRegexes?: string[];
  metadataRows?: number[];
  sourceLocale?: string;
  html?: {
    enabled?: boolean;
    translatableTags?: string[];
  };
};

export type SheetMapping = {
  sheetName: string;
  enabled: boolean;
  sourceColumn: string;
  sourceLocale: string;
  targetColumns: Array<{ locale: string; column: string }>;
  inlineCodeRegexes: string[];
  headerRow: number;
  valuesStartRow: number;
  metadataRows: number[];
  skipHiddenRows: boolean;
  skipHiddenColumns: boolean;
  translateComments: boolean;
  treatMerged: SheetConfig['treatMergedRegions'];
  preserveStyles: boolean;
  excludeColors: string[];
  htmlEnabled: boolean;
  htmlTranslatableTags: string;
  // New fields for limits
  rowCount?: number;
};

export type SegmentationMode = 'builtin' | 'srx';

export type SegmentationOptions = {
  enabled: boolean;
  mode: SegmentationMode;
  srxText?: string;
};

export type MTProvider = 'none' | 'google' | 'deepl';

export type GlobalOptions = {
  srcLang: string;
  exportComments: boolean;
  mergeFallback: 'source' | 'empty';
  insertTargetPlacement: 'insertAfterSource' | 'appendToSheetEnd';
  overwrite: boolean;
  targetLocale?: string;
  xliffVersion?: '1.2' | '2.1';
  mtProvider?: MTProvider;
};

export type Config = {
  workbook: {
    sheets: SheetConfig[];
  };
  global?: {
    srcLang?: string;
    targetLocale?: string;
    exportComments?: boolean;
    mergeFallback?: 'source' | 'empty';
    insertTargetPlacement?: 'insertAfterSource' | 'appendToSheetEnd';
    overwrite?: boolean;
    xliffVersion?: '1.2' | '2.1';
    mtProvider?: MTProvider;
  };
  segmentation?: {
    enabled?: boolean;
    rules?: 'builtin' | { srxPath: string };
  };
};

export enum PlanType {
  FREE = 'free',
  STARTER = 'starter',
  PRO = 'pro',
  AGENCY = 'agency'
}

export interface PlanLimits {
  credits: number;
  maxRowsPerFile: number;
  maxTargetLangs: number;
  customSrx: boolean;
  apiAccess: boolean;
  rollover: boolean;
  prioritySupport: boolean;
  prefillMT: boolean;
}

export interface User {
  userId: string;
  email: string;
  credits: number;
  currentPlan: PlanType;
  limits: PlanLimits;
  subscriptionStatus?: string;
  nextBillingDate?: string;
  paddleSubscriptionId?: string;
}
